'use client';

import { ALL_STATES } from '@/constants/location_states';
import {
  buildPanchangDateQuery,
  buildPanchangDistrictPath,
  getTodayPanchangDate,
  inputValueToPanchangDate,
  panchangDateToInputValue,
} from '@/lib/panchang';
import { toCleanSlug } from '@/lib/location_slug_mapper';
import { useLazyAllDistricts } from '@/hooks/useLazyAllDistricts';
import { useRouter } from 'next/navigation';
import { FormEvent, useEffect, useMemo, useState } from 'react';

interface PanchangFiltersProps {
  initialDate: string;
  stateInternalSlug: string;
  districtInternalSlug: string;
}

export default function PanchangFilters({
  initialDate,
  stateInternalSlug,
  districtInternalSlug,
}: PanchangFiltersProps) {
  const router = useRouter();
  const [date, setDate] = useState(initialDate || getTodayPanchangDate());
  const [stateSlug, setStateSlug] = useState(stateInternalSlug);
  const [districtSlug, setDistrictSlug] = useState(districtInternalSlug);
  const allDistricts = useLazyAllDistricts();

  useEffect(() => {
    setDate(initialDate || getTodayPanchangDate());
    setStateSlug(stateInternalSlug);
    setDistrictSlug(districtInternalSlug);
  }, [initialDate, stateInternalSlug, districtInternalSlug]);

  const stateOptions = useMemo(
    () =>
      Object.entries(ALL_STATES).map(([slug, state]) => ({
        slug,
        label: state.label || state.inline_label || state.label_string,
        code: state.value,
      })),
    [],
  );

  const selectedStateCode = stateOptions.find((option) => option.slug === stateSlug)?.code;

  const districtOptions = useMemo(() => {
    if (!selectedStateCode || !allDistricts) {
      return [];
    }
    const districtMap = allDistricts[selectedStateCode] || {};
    return Object.entries(districtMap).map(([slug, district]) => ({
      slug,
      label: district.label || district.name_hi,
      code: district.value,
    }));
  }, [selectedStateCode, allDistricts]);

  const selectedStateLabel = stateOptions.find((option) => option.slug === stateSlug)?.label;
  const selectedDistrictLabel = districtOptions.find(
    (option) => option.slug === districtSlug,
  )?.label;

  const filterSummary = [date, selectedDistrictLabel || selectedStateLabel]
    .filter(Boolean)
    .join(' · ');

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextStateClean = toCleanSlug(stateSlug);
    const nextDistrictClean = toCleanSlug(districtSlug);

    if (!stateSlug) {
      router.push('/panchang');
      return;
    }

    if (!districtSlug) {
      router.push(`/panchang/${nextStateClean}${buildPanchangDateQuery(date)}`);
      return;
    }

    router.push(
      buildPanchangDistrictPath({
        stateCleanSlug: nextStateClean,
        districtCleanSlug: nextDistrictClean,
        date,
      }),
    );
  };

  return (
    <details className='group mx-4 rounded-xl border border-[#8b5e3c]/30 bg-[#fff8ef]/90 shadow-sm backdrop-blur-sm'>
      <summary className='flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-3 text-sm font-semibold text-[#772512] [&::-webkit-details-marker]:hidden'>
        <span className='min-w-0'>
          <span className='block'>फ़िल्टर</span>
          {filterSummary ? (
            <span className='mt-0.5 block truncate text-xs font-normal text-[#5c3d28]'>
              {filterSummary}
            </span>
          ) : null}
        </span>
        <span className='flex-shrink-0 text-base text-[#8b5e3c] group-open:hidden'>▼</span>
        <span className='hidden flex-shrink-0 text-base text-[#8b5e3c] group-open:inline'>
          ▲
        </span>
      </summary>
      <form onSubmit={onSubmit} className='border-t border-[#d8b995] px-3 pb-3 pt-2'>
        <div className='grid gap-2 sm:grid-cols-2'>
          <label className='flex flex-col gap-1 text-xs text-[#5c3d28]'>
            <span className='font-semibold'>तारीख</span>
            <input
              type='date'
              className='rounded-md border border-[#c9a27b] bg-white px-3 py-2 text-sm text-[#3d2819]'
              value={panchangDateToInputValue(date)}
              onChange={(event) => {
                const nextDate = inputValueToPanchangDate(event.target.value);
                if (nextDate) {
                  setDate(nextDate);
                }
              }}
            />
          </label>
          <label className='flex flex-col gap-1 text-xs text-[#5c3d28]'>
            <span className='font-semibold'>राज्य</span>
            <select
              className='rounded-md border border-[#c9a27b] bg-white px-3 py-2 text-sm text-[#3d2819]'
              value={stateSlug}
              onChange={(event) => {
                setStateSlug(event.target.value);
                setDistrictSlug('');
              }}
            >
              <option value=''>राज्य चुनें</option>
              {stateOptions.map((state) => (
                <option key={state.slug} value={state.slug}>
                  {state.label}
                </option>
              ))}
            </select>
          </label>
          <label className='flex flex-col gap-1 text-xs text-[#5c3d28] sm:col-span-2'>
            <span className='font-semibold'>जिला</span>
            <select
              className='rounded-md border border-[#c9a27b] bg-white px-3 py-2 text-sm text-[#3d2819]'
              value={districtSlug}
              disabled={!stateSlug}
              onChange={(event) => setDistrictSlug(event.target.value)}
            >
              <option value=''>जिला चुनें</option>
              {districtOptions.map((district) => (
                <option key={district.slug} value={district.slug}>
                  {district.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          type='submit'
          className='mt-3 w-full rounded-lg bg-[#772512] px-4 py-2 text-sm font-semibold text-white'
        >
          अपडेट करें
        </button>
      </form>
    </details>
  );
}
